/**
 * @license
 * Copyright 2020 Google Inc.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

 import './tool_control.less';

 import {WatchableValueInterface} from 'neuroglancer/trackable_value';
 import {RefCounted} from 'neuroglancer/util/disposable';
//  import { ConfigDialog } from 'neuroglancer/ui/congfig-dialog';
 import {ConfigStorage } from 'neuroglancer/viewer_state';
 import svg_down from 'neuroglancer/static/down.svg';
import { TrackableBoolean } from '../trackable_boolean';
import { viewer } from 'src/main';
import i18n from '../i18n/i18n';
import svg_info from 'neuroglancer/static/info.svg';
import {Tooltip} from "neuroglancer/widget/tooltip";
 
 export interface ToolControlOption {
    label?: string;
    classes?: Array<string>;
    onChange?: Function;
    configStorage?: ConfigStorage;
    isDownIcon?: boolean
 }
interface TooltipInfo {
  title?: string;
  width?: number;
}

 export interface ToolControlInputOption {
    label?: string;
    labelInfo?: TooltipInfo;
    suffix?: string;
    classes?: Array<string>;
    onChange?: Function,
    type?: string,
    step?: string,
    max?: string,
    min?:string
 }
 
 export class ToolControl extends RefCounted {
   readonly element: HTMLElement;
   localDrop: boolean = false;
   childElement: HTMLElement;
   controlElement: HTMLElement;
   name: string
   disabled = new TrackableBoolean (false, false);
   constructor(model: WatchableValueInterface<boolean> | null, options: ToolControlOption) {
    super();
    this.element = document.createElement('div')
    this.element.classList.add('tool-control')
    const {label = '', onChange, configStorage, isDownIcon} = options

    const labelItem = document.createElement('span')
    labelItem.innerHTML = label
    this.element.appendChild(labelItem)

    const leftBox = document.createElement('div')
    const inputItem = document.createElement('input')
    inputItem.type = 'checkbox'
    inputItem.classList.add('ng-common-switch')
    inputItem.id = label
    const inputLabel = document.createElement('label')
    inputLabel.setAttribute('for', label)
    leftBox.appendChild(inputItem)
    leftBox.appendChild(inputLabel)

    const span = document.createElement('div')
    span.classList.add('down-icon')
    span.innerHTML = svg_down
    if(!isDownIcon && isDownIcon !== undefined)span.style.display = 'none';
    leftBox.appendChild(span)
    this.element.appendChild(leftBox)
    this.element.classList.add('tool-control-'+label.replace(/\s*/g,""));
    
    if (configStorage) {
      configStorage.registConfig(label, this.element, label)
      span.classList.add('visible')
      const clickHandle = () => {
        if (this.disabled.value) return
        let flag = this.element.dataset.selected === 'true'
        if (configStorage.activedTool.value === i18n.color_bar && viewer.showColorbar.value && inputItem.checked){
          configStorage.activedTool.value = configStorage.activedTool.value ? '' : label;
          configStorage.activedTool.value = label;
        }else{
          configStorage.activedTool.value = flag ? '' : label;
        }
      }
      span.addEventListener('click', clickHandle)
      labelItem.addEventListener('click', clickHandle)
      configStorage.activedTool.changed.add(() => {
        // this.element.dataset.selected = configStorage.activedTool.value === label ? 'true' : 'false';
        if(configStorage.activedTool.value === label && configStorage.activedTool.value === i18n.color_bar){
          this.element.dataset.selected = (viewer.showColorbar.value).toString();
        }else{
          this.element.dataset.selected = configStorage.activedTool.value === label ? 'true' : 'false';
        }
      })
    }

    inputItem.addEventListener('change', () => {
        if (model) {
            model.value = !model.value
        } else {
            this.localDrop = !this.localDrop
        }
        // if (model?.value || this.localDrop) {
        //   new ConfigDialog(this.element)
        // }
        if (onChange !== undefined) {
          onChange();
        }
        viewer.resetoreJson(viewer.state.toJSON());
    })

    const updateView = () => {
      const value = model ? model.value : this.localDrop;
      this.element.dataset.checked = value ? 'true' : 'false';
      inputItem.checked = value
      // value ? inputItem.setAttribute('checked', 'checked') :  inputItem.removeAttribute('checked')
      this.element.classList[this.disabled.value ? 'add' : 'remove']('ng-common-disabled');
      if (onChange !== undefined) {
        onChange()
      }
      // if (this.childElement) this.childElement.classList[value ? 'add': 'remove']('show')
    };
    if (model) this.registerDisposer(model.changed.add(updateView));
    this.registerDisposer(this.disabled.changed.add(updateView))
    updateView();
   }
   setDisable (val: boolean) {
    this.disabled.value = val
   }
 }

 export class ToolControlInput extends RefCounted {
   readonly element: HTMLElement;
   localDrop: boolean = false;
   childElement: HTMLElement;
   controlElement: HTMLElement;
   input: HTMLInputElement;
   name: string
   constructor(model: WatchableValueInterface<boolean> | null, options: ToolControlInputOption) {
    super();
    this.element = document.createElement('div')
    this.element.classList.add('tool-control')
    const {label = '', onChange, step, max, min,suffix, labelInfo} = options

    const labelItem = document.createElement('span')
    labelItem.innerHTML = label;
    this.element.appendChild(labelItem);

    if (labelInfo) {
      const infoItem = document.createElement('span');
      infoItem.classList.add('tool-control-info');
      infoItem.innerHTML = svg_info;
      labelItem.appendChild(infoItem);
      const tooltip = this.registerDisposer(new Tooltip());
      let hideTimer: any;
      tooltip.element.innerHTML = `<div>${labelInfo.title}<div/>`;
      let tooltipWidth = labelInfo.width ?? 200;
      tooltip.element.style.width = tooltipWidth + 'px';
      infoItem.addEventListener('mouseenter', ()=>{
        clearTimeout(hideTimer);
        const rect = infoItem.getBoundingClientRect();
        tooltip.updatePosition(rect.left + (rect.width  - tooltipWidth) * 0.5, rect.bottom);
      })
      infoItem.addEventListener('mouseleave', ()=>{
        hideTimer = setTimeout(()=>{
          tooltip.element.style.visibility = 'hidden';
        },500)
      })
    }

    const leftBox = document.createElement('div')
    const inputItem = this.input = document.createElement('input')
    inputItem.type = 'number';
    inputItem.step = step??'1';
    inputItem.max = max??'180';
    inputItem.min = min??'-180';
    inputItem.classList.add('ng-common-input', 'tool-control-input-text')
    leftBox.appendChild(inputItem)
    if (suffix) {
      const suffixItem = document.createElement('span');
      suffixItem.classList.add('tool-control-suffix')
      suffixItem.innerHTML = suffix;
      leftBox.appendChild(suffixItem)
    }
    this.element.appendChild(leftBox)

    // inputItem.addEventListener('blur', () => {
    //   if (onChange !== undefined) {
    //     onChange(inputItem.value)
    //   }
    // })
    inputItem.addEventListener('change', () => {
      if (onChange !== undefined) {
        onChange(inputItem.value)
      }
    })
    inputItem.addEventListener('keyup', (e) => {
      if ((e.code.toLocaleLowerCase() === 'enter' || e.code.toLocaleLowerCase() === 'numpadenter') && onChange !== undefined) {
        const transform = document.getElementsByClassName('neuroglancer-icon-transform')[0]?.getAttribute('data-checked') ?? 'false';
        if(transform === 'true') return
        onChange(inputItem.value)
      }
    })

    inputItem.addEventListener('input', () => {
      if (Number(inputItem.valueAsNumber) > Number(inputItem.max)) inputItem.valueAsNumber = Number(inputItem.max)
      if (Number(inputItem.valueAsNumber) < Number(inputItem.min)) inputItem.valueAsNumber = Number(inputItem.min)
    })

    const updateView = () => {
      // const value = model ? model.value : this.localDrop;
      // this.element.dataset.checked = value ? 'true' : 'false';
      // value ? inputItem.setAttribute('checked', 'checked') :  inputItem.removeAttribute('checked')

    };
    if (model) this.registerDisposer(model.changed.add(updateView));
    updateView();
   }

   setInputValue (val: string) {
    this.input.value = val
   }
 }
